import React, { useEffect, useRef, useState } from "react";
import { createLeagueSheetSignedUrl } from "../../utility/averageCatalogs";
import { displaySheetPage } from "../../utility/sheetHighlight";
import {
  SheetRowPreview,
  isPdfFile,
} from "../organizations/AverageCatalogs/SheetRowPreview";

export const SignupSheetPreview = ({
  storagePath,
  mimeType,
  fileName,
  sourcePage,
  highlightName = "",
  highlightNames = [],
  boundingBox = null,
  compact = false,
}) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [paneSize, setPaneSize] = useState({ width: 640, height: 180 });
  const paneRef = useRef(null);
  const pageNumber = displaySheetPage(sourcePage);
  const pdf = isPdfFile(mimeType, fileName);

  useEffect(() => {
    let cancelled = false;
    if (!storagePath) {
      setUrl("");
      setError("This signup sheet file is not stored.");
      return undefined;
    }
    setError("");
    setUrl("");
    createLeagueSheetSignedUrl(storagePath)
      .then((signedUrl) => {
        if (!cancelled) setUrl(signedUrl);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setError(err.message || "Could not open this signup sheet.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  useEffect(() => {
    if (!paneRef.current) return undefined;
    const pane = paneRef.current;
    const updateSize = () =>
      setPaneSize({
        width: pane.clientWidth || 640,
        height: pane.clientHeight || 180,
      });
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [url]);

  return (
    <div
      className={`overflow-hidden flex flex-col ${
        compact
          ? "sticky top-0 z-20 card-board h-[20vh] min-h-[10rem] max-h-[12.5rem]"
          : "card-board h-full min-h-[28rem]"
      }`}
    >
      {compact ? null : (
        <div className="px-4 py-3 border-b border-brand-charcoal/10">
          <h2 className="heading-display text-lg">Signup sheet</h2>
          <p className="text-sm text-brand-muted mt-1">
            {[fileName, pageNumber ? `Page ${pageNumber}` : null]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      )}
      <div className={`flex-1 min-h-0 ${compact ? "p-1" : "p-2"}`}>
        {error ? (
          <p className="text-sm text-brand-charcoal">{error}</p>
        ) : !url ? (
          <p className="text-sm text-brand-muted">Opening signup sheet.</p>
        ) : pdf && sourcePage == null ? (
          <iframe
            title={fileName || "Signup sheet"}
            src={url}
            className="w-full h-full min-h-[8rem] border border-brand-charcoal/10 rounded-lg bg-white"
          />
        ) : (
          <div
            ref={paneRef}
            className={`h-full min-h-0 overflow-hidden ${
              compact ? "" : "border border-brand-charcoal/10 rounded-lg bg-brand-cream p-2"
            }`}
          >
            <SheetRowPreview
              url={url}
              storagePath={storagePath}
              mimeType={mimeType}
              fileName={fileName}
              sourcePage={sourcePage}
              highlightName={highlightName}
              highlightNames={highlightNames}
              boundingBox={boundingBox}
              preferBoundingBox
              fitHighlight={compact}
              hideZoomBar={compact}
              paintHighlight={false}
              containerWidth={paneSize.width}
              containerHeight={paneSize.height}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupSheetPreview;
